import { Box, Flex, Text } from '@chakra-ui/react'
import React from 'react'
import FastAvatar from '../../image/FastAvatar'

const TONES = [
  { color: '#5D5ECB', bg: '#EFEFFB' },
  { color: '#B7791F', bg: '#FEF5E6' },
  { color: '#0E8A7E', bg: '#E4F6F3' },
  { color: '#C2477A', bg: '#FCEAF2' },
  { color: '#2F80C9', bg: '#E8F2FB' },
  { color: '#7A5AC8', bg: '#F3EEFC' },
]

/** A stable colour per name, so the same person always looks the same. */
export const personTone = (name: string) => {
  let hash = 0
  for (let i = 0; i < name.length; i += 1) hash = (hash * 31 + name.charCodeAt(i)) >>> 0
  return TONES[hash % TONES.length]
}

interface PersonCellProps {
  name: string
  image?: string | null
  subtitle?: React.ReactNode
  size?: number
  /** Muted grey name, for "nobody" rather than a real person. */
  muted?: boolean
}

/**
 * Person with a photo, or coloured initials when there is none, instead of a
 * blank grey circle. Used by the workforce tables.
 */
const PersonCell: React.FC<PersonCellProps> = ({ name, image, subtitle, size = 34, muted }) => {
  const tone = personTone(name)
  const initials = name.split(' ').filter(Boolean).slice(0, 2).map((part) => part[0]?.toUpperCase()).join('')
  return (
    <Flex align="center" gap="11px" minW={0}>
      {image ? (
        <FastAvatar boxSize={`${size}px`} name={name} src={image} flexShrink={0} />
      ) : (
        <Flex
          w={`${size}px`}
          h={`${size}px`}
          flexShrink={0}
          align="center"
          justify="center"
          borderRadius="full"
          bg={muted ? 'solid.25' : tone.bg}
          color={muted ? 'solid.300' : tone.color}
          fontSize={size >= 32 ? '12.5px' : '10.5px'}
          fontWeight="800"
        >
          {initials || '?'}
        </Flex>
      )}
      <Box minW={0}>
        <Text fontSize="13.5px" fontWeight="650" color={muted ? 'solid.400' : 'solid.900'} noOfLines={1} title={name}>{name}</Text>
        {subtitle && (typeof subtitle === 'string'
          ? <Text fontSize="12px" color="solid.500" noOfLines={1} title={subtitle}>{subtitle}</Text>
          : subtitle)}
      </Box>
    </Flex>
  )
}

export default PersonCell
